import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import EmailInboxTable from '../components/ContactCenter/EmailInboxTable';
import { fmtAsOf, fmtCount, fmtClock, fmtPct } from '../components/ContactCenter/format';
import { c, card } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Email — the support inboxes' backlog: how many emails wait
// in each, how long the oldest has, the share answered within target over the
// last 24 hours, and today's handled. One card per inbox, worst first, with
// the old cards' colour rule (services/ccEmailRead.js).
//
// Two views, as the old page had: cards by default, the sortable table one
// toggle away. They answer different questions — the cards are ordered for
// you and say whether each inbox is meeting its OWN target; the table lets
// you sort by a column and compare inboxes against a fixed scale. Unlike the
// old app, which mounted one view at a time so only it polled, both read the
// one query this page already runs: it is the same 30-second poll either way,
// and switching view costs no request.

const BAND = {
  red: { label: 'Behind', bg: c.dangerTint, fg: c.dangerText },
  amber: { label: 'At risk', bg: c.warningTint, fg: c.warningText },
  green: { label: 'On track', bg: c.successTint, fg: c.successText },
  empty: { label: 'Empty', bg: c.surfaceSunk, fg: c.textMuted },
};

// Email ages run to days: "< 1h", "5h", "2d 3h", "9d".
export function fmtAge(secs) {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  if (h < 1) return '< 1h';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return d >= 7 || h % 24 === 0 ? `${d}d` : `${d}d ${h % 24}h`;
}
const fmtTarget = (secs) => (secs % 86400 === 0 ? `${secs / 86400} day${secs === 86400 ? '' : 's'}` : `${Math.round(secs / 3600)} h`);

function InboxCard({ i }) {
  const b = BAND[i.band];
  return (
    <section style={{ ...s.card, boxShadow: i.band === 'empty' ? 'none' : `inset 3px 0 0 ${b.fg}` }} aria-label={i.name}>
      <div style={s.head}>
        <span style={s.name} title={i.email || undefined}>{i.name}</span>
        <span style={{ ...s.pill, background: b.bg, color: b.fg }}>{b.label}</span>
      </div>
      <div style={s.big}>{fmtCount(i.depth)} <span style={s.unit}>waiting</span></div>
      <div style={s.sub}>{i.depth > 0 ? `oldest ${fmtAge(i.oldestSecs)}` : 'nothing waiting'}</div>
      <dl style={s.facts}>
        <div><dt style={s.dt}>Within target, 24 h</dt><dd style={s.dd}>{fmtPct(i.sla)}</dd></div>
        <div><dt style={s.dt}>Target</dt><dd style={s.dd}>{fmtPct(i.slaPct)} in {fmtTarget(i.slaSeconds)}</dd></div>
        <div><dt style={s.dt}>Handled today</dt><dd style={s.dd}>{fmtCount(i.handledToday)}</dd></div>
        <div><dt style={s.dt}>AHT today</dt><dd style={s.dd}>{fmtClock(i.ahtToday)}</dd></div>
      </dl>
    </section>
  );
}

const VIEWS = [['cards', 'Cards'], ['table', 'Table']];

export default function ContactCenterEmail() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState('cards');
  // The table hides each inbox's address until asked, as the old one did.
  const [showAddresses, setShowAddresses] = useState(false);
  const q = useQuery({
    queryKey: ['cc-email'],
    queryFn: () => api.get('/contact-center/email'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const d = q.data;
  const needle = search.trim().toLowerCase();
  const shown = (d?.inboxes || []).filter((i) => !needle
    || i.name.toLowerCase().includes(needle) || (i.email || '').toLowerCase().includes(needle));
  const count = (band) => (d?.inboxes || []).filter((i) => i.band === band).length;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Contact Center"
        title="Email"
        subtitle={d ? `${d.inboxes.length} inboxes · ${count('red')} behind · ${count('amber')} at risk · ${count('green')} on track · ${count('empty')} empty` : 'Support inboxes'}
        meta={d?.observedAt ? <span style={{ fontSize: '11px', color: d.stale ? c.dangerText : c.textMuted }}>Updated {fmtAsOf(d.observedAt)}</span> : null}
        actions={(
          <div style={s.actions}>
            <span style={s.seg} role="group" aria-label="View">
              {VIEWS.map(([id, label]) => (
                <button key={id} type="button" aria-pressed={view === id} onClick={() => setView(id)}
                  style={view === id ? s.segOn : s.segOff}>{label}</button>
              ))}
            </span>
            {view === 'table' && (
              <button type="button" aria-pressed={showAddresses} onClick={() => setShowAddresses((v) => !v)}
                style={{ ...s.segOff, border: `1px solid ${c.border}` }}>
                {showAddresses ? 'Hide addresses' : 'Show addresses'}
              </button>
            )}
            <input id="cc-email-search" type="search" aria-label="Search inboxes" placeholder="Search inboxes" value={search}
              onChange={(e) => setSearch(e.target.value)} style={s.input} />
          </div>
        )}
        alignActions="end"
      />
      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the inboxes: {q.error.message}</div>}
      {d?.stale && (
        <div style={s.stale} role="alert">
          {d.observedAt ? `The live counts stopped updating at ${fmtAsOf(d.observedAt)}.` : 'The live worker has not reported yet.'} What is waiting may be out of date.
        </div>
      )}
      {d && (
        <>
          {view === 'cards' ? (
            <>
              <div style={s.grid}>{shown.map((i) => <InboxCard key={i.queueId} i={i} />)}</div>
              {!shown.length && <p style={s.foot}>No inbox matches.</p>}
            </>
          ) : (
            <EmailInboxTable inboxes={shown} fmtAge={fmtAge} fmtTarget={fmtTarget} showAddresses={showAddresses} />
          )}
          <p style={s.foot}>
            Behind: the oldest email has waited past the inbox&rsquo;s target time, or fewer than the target share were
            answered within it over the last 24 hours. At risk: the oldest has waited three-quarters of the target time.
            The 24 hours are whole hours: the 24 before this one and this one so far. Targets are the ones Genesys held
            for each inbox in May 2026; the portal cannot read queue settings yet.
            {view === 'table' && ' The table colours each column against a fixed scale rather than against each'
              + ' inbox\u2019s own target, so a glance down one column compares like with like.'}
          </p>
        </>
      )}
    </div>
  );
}

const s = {
  page:  { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '12px' },
  card:  { ...card.base, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px' },
  head:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  name:  { fontSize: '13px', fontWeight: 600, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pill:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap' },
  big:   { fontSize: '26px', fontWeight: 650, color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  unit:  { fontSize: '12px', fontWeight: 400, color: c.textMuted },
  sub:   { fontSize: '11px', color: c.textMuted },
  facts: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px', margin: '6px 0 0' },
  dt:    { fontSize: '10px', color: c.textMuted },
  dd:    { margin: 0, fontSize: '13px', fontWeight: 600, color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  actions: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  seg:    { display: 'inline-flex', gap: '2px', background: c.surfaceSunk, borderRadius: '8px', padding: '3px' },
  segOff: { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textMuted },
  segOn:  { all: 'unset', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: c.textStrong, background: c.surface, boxShadow: '0 1px 2px rgba(15,23,42,0.08)' },
  input: { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface, minWidth: '200px' },
  error: { fontSize: '13px', color: c.dangerText },
  stale: { fontSize: '13px', fontWeight: 600, color: c.dangerText, background: c.dangerTint, padding: '10px 14px', borderRadius: '8px' },
  foot:  { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.5 },
};
